<script setup lang="ts">
import { capitalize, computed, defineProps } from "vue";
import { FavorableDayReport, MonthDayReport } from "@/types/calculators";
import { useI18n } from "vue-i18n";
import { useLangStore } from "@/stores/langStore";
import { formatDate, getWeekdayName } from "@/utils/date";

const props = defineProps<{
  selectedCard: MonthDayReport;
  dayDetailsInfo: FavorableDayReport;
  timezone: string;
  latitude: number;
  longitude: number;
}>();

const { t } = useI18n();
const langStore = useLangStore();

const isBadBackground = computed(() => {
  const badCodes = ["下下", "下"];

  return badCodes.includes(
    props.dayDetailsInfo.day_report.general.yi_ji_activities.conflict_grade
      .rank_code
  );
});
const isBaziBad = computed(() => {
  return (
    props.dayDetailsInfo.day_report.personal.verdict.split(" ")[0] === "bad"
  );
});
</script>

<template>
  <header
    class="bg-white rounded-2xl border border-divider p-4 sm:p-6 shadow-sm"
  >
    <div
      class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5 pb-5 border-b border-divider"
    >
      <div>
        <div class="flex flex-wrap items-center gap-2 mb-2">
          <span
            class="text-xs uppercase tracking-wider font-semibold px-2.5 py-0.5 rounded-full bg-beige text-active border border-active/10"
          >
            {{ t("calculators.personal-date.full-day-report.title") }}
          </span>
          <span class="text-xs text-muted flex items-center gap-1">
            <span>📍 {{ timezone }}</span>
            <span>
              • {{ longitude.toFixed(2) }}°E, {{ latitude.toFixed(2) }}°N
            </span>
          </span>
        </div>
        <h1
          class="text-2xl sm:text-3xl font-bold text-brown flex flex-wrap items-center gap-2 sm:gap-3"
        >
          <span>{{ formatDate(selectedCard.date, langStore.current) }}</span>
          <span class="text-base sm:text-lg font-normal text-muted font-caudex">
            ({{
              capitalize(getWeekdayName(selectedCard.date, langStore.current))
            }})
          </span>
        </h1>
        <p
          class="text-xs sm:text-sm text-alternative-gray mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1"
        >
          <span>
            {{ t("calculators.personal-date.full-day-report.month-calendar") }}
            <strong class="text-brown">
              {{ selectedCard.lunar_short }}
            </strong>
          </span>
          <span class="text-muted">•</span>
          <span>
            {{ t("calculators.personal-date.full-day-report.season") }}
            <strong class="text-brown">
              {{ dayDetailsInfo.day_report.general.solar_term.current.name }}
            </strong>
            ({{
              t("calculators.personal-date.full-day-report.current-solar-day", {
                day: dayDetailsInfo.day_report.general.solar_term
                  .days_since_month_jie,
              })
            }})
          </span>
          <span class="text-muted">•</span>
          <span>
            {{ t("calculators.personal-date.full-day-report.pillars") }}
            <strong class="font-caudex text-active">
              {{ selectedCard.pillars.year }} •
              {{ selectedCard.pillars.month }} • {{ selectedCard.pillars.day }}
            </strong>
          </span>
        </p>
      </div>
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div
          class="px-3 py-2.5 bg-secondary rounded-xl border border-divider text-center"
        >
          <div class="text-[10px] sm:text-[11px] text-muted">
            {{ t("calculators.personal-date.full-day-report.officers") }}
          </div>
          <div class="text-sm font-bold text-active mt-0.5 font-caudex">
            {{ dayDetailsInfo.day_report.general.twelve_jian_chu_officer.name }}
            <span class="text-xs font-normal text-brown block">
              ({{
                dayDetailsInfo.day_report.general.twelve_jian_chu_officer
                  .title_ua
              }})
            </span>
          </div>
        </div>
        <div
          class="px-3 py-2.5 bg-secondary rounded-xl border border-divider text-center"
        >
          <div class="text-[10px] sm:text-[11px] text-muted">
            {{ t("calculators.personal-date.full-day-report.constellations") }}
          </div>
          <div class="text-sm font-bold text-brown mt-0.5 font-caudex">
            {{ dayDetailsInfo.day_report.general.constellation_28.full_name }}
            <span
              class="text-xs font-normal block"
              :class="
                dayDetailsInfo.day_report.general.constellation_28.is_auspicious
                  ? 'text-success'
                  : 'text-error'
              "
            >
              {{
                dayDetailsInfo.day_report.general.constellation_28.is_auspicious
                  ? t("calculators.personal-date.full-day-report.favor-const")
                  : t("calculators.personal-date.full-day-report.taboo-const")
              }}
            </span>
          </div>
        </div>
        <div
          class="px-3 py-2.5 bg-secondary rounded-xl border border-divider text-center"
        >
          <div class="text-[10px] sm:text-[11px] text-muted">
            {{ t("calculators.personal-date.full-day-report.month-hexagram") }}
          </div>
          <div class="text-sm font-bold text-brown mt-0.5 font-caudex">
            {{ dayDetailsInfo.day_report.general.month_hexagram_pi_gua.name }}
            <span class="text-[11px] text-muted block">
              {{
                t(
                  "calculators.personal-date.full-day-report.yin-yang-hexagram",
                  {
                    yin: dayDetailsInfo.day_report.general.month_hexagram_pi_gua
                      .yin_lines,
                    yang: dayDetailsInfo.day_report.general
                      .month_hexagram_pi_gua.yang_lines,
                  }
                )
              }}
            </span>
          </div>
        </div>
        <div
          class="px-3 py-2.5 bg-secondary rounded-xl border border-divider text-center"
        >
          <div class="text-[10px] sm:text-[11px] text-muted">
            {{ t("calculators.personal-date.full-day-report.sun-general") }}
          </div>
          <div class="text-sm font-bold text-gold mt-0.5 font-caudex">
            {{
              dayDetailsInfo.day_report.general.sun_position_yue_jiang.branch
            }}
            ({{
              dayDetailsInfo.day_report.general.sun_position_yue_jiang
                .spirit_general_name
            }})
            <span class="text-[11px] text-muted block">
              {{
                dayDetailsInfo.day_report.general.sun_position_yue_jiang
                  .palace_name
              }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
      <div
        class="p-4 sm:p-5 rounded-xl border"
        :class="
          isBadBackground
            ? 'border-error/30 bg-[#FDF7F7]'
            : 'border-success/30 bg-[#F6FAF7]'
        "
      >
        <div class="flex items-start gap-3.5">
          <div
            class="w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shrink-0 font-bold text-xl"
            :class="
              isBadBackground
                ? 'bg-error/10 text-error'
                : 'bg-success/10 text-success'
            "
          >
            {{ isBadBackground ? "✕" : "✓" }}
          </div>
          <div>
            <div class="flex flex-wrap items-center gap-2">
              <span
                class="text-xs font-bold uppercase tracking-wider text-muted"
              >
                {{
                  t(
                    "calculators.personal-date.full-day-report.lunar-day-background"
                  )
                }}
              </span>
              <span
                class="px-2 py-0.5 rounded text-[11px] font-bold text-white"
                :class="isBadBackground ? 'bg-error' : 'bg-success'"
              >
                {{ t("calculators.personal-date.top-days.rang") }}
                {{
                  dayDetailsInfo.day_report.general.yi_ji_activities
                    .conflict_grade.rank_code
                }}
              </span>
            </div>
            <h3 class="text-sm sm:text-base font-bold text-brown mt-1">
              {{
                dayDetailsInfo.day_report.general.yi_ji_activities
                  .conflict_grade.rank_title
              }}
            </h3>
            <p class="text-xs text-alternative-gray mt-1 leading-relaxed">
              {{
                dayDetailsInfo.day_report.general.yi_ji_activities
                  .conflict_grade.explanation
              }}
            </p>
          </div>
        </div>
      </div>
      <div
        class="p-4 sm:p-5 rounded-xl border"
        :class="
          isBaziBad
            ? 'border-error/30 bg-[#FDF7F7]'
            : 'border-success/30 bg-[#F6FAF7]'
        "
      >
        <div class="flex items-start gap-3.5">
          <div
            class="w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shrink-0 font-bold text-lg sm:text-xl"
            :class="
              isBaziBad
                ? 'bg-error/10 text-error'
                : 'bg-success/10 text-success'
            "
          >
            {{ dayDetailsInfo.day_report.personal.compatibility_score }}%
          </div>
          <div>
            <div class="flex flex-wrap items-center gap-2">
              <span
                class="text-xs font-bold uppercase tracking-wider text-muted"
              >
                {{
                  t(
                    "calculators.personal-date.full-day-report.personal-bazi-compatibility"
                  )
                }}
              </span>
              <span
                class="px-2 py-0.5 rounded text-[11px] font-bold text-white"
                :class="isBaziBad ? 'bg-error' : 'bg-success'"
              >
                {{ dayDetailsInfo.day_report.personal.verdict.split(" ")[0] }}
              </span>
            </div>
            <h3 class="text-sm sm:text-base font-bold text-brown mt-1">
              {{ dayDetailsInfo.day_report.personal.verdict }}
            </h3>
            <i18n-t
              keypath="calculators.personal-date.full-day-report.day-master-takes-energy"
              tag="p"
              class="text-xs text-alternative-gray mt-1 leading-relaxed"
            >
              <template #dayMaster>
                <strong class="text-brown">
                  {{ dayDetailsInfo.user_natal_summary.day_master.element }}
                  ({{ dayDetailsInfo.user_natal_summary.day_master.element }})
                </strong>
              </template>
              <template #stemGod>
                <strong class="text-brown">
                  {{
                    dayDetailsInfo.day_report.personal.ten_gods
                      .relative_to_day_master.day_stem_ten_god.name
                  }}
                  ({{
                    dayDetailsInfo.day_report.personal.ten_gods
                      .relative_to_day_master.day_stem_ten_god.ua
                  }})
                </strong>
              </template>
            </i18n-t>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>
